import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { usePersonalizationWidgetData } from '@vp/design-personalization-widget'
import type { QuickViewAnalyticsCallbacks, DesignVariationResponse, SimilarTemplateEntity } from '@vp/quick-view-modal'

import { useQuickViewAnalytics } from '~/app/features/Gallery/components/PreviewArea/GalleryQuickView/hooks/useQuickViewAnalytics'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { useProductOptionsTracking } from '~/app/features/Gallery/hooks/useProductOptionsTracking'
import { galleryImpressionIdSelector } from '~/app/store/analytics/reducer'
import { getIsDirectQuickView, getTakeOverQV } from '~/app/store/debug/reducer'
import { designPersonalizationContextIdSelector } from '~/app/store/designPersonalization/selectors'
import { selectQuickViewFilterNames } from '~/app/store/quickViewFilter'
import { globalQuickViewDataSelector } from '~/app/store/quickView/selectors'
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_LABEL, ANALYTICS_EVENT_DETAILS, AnalyticsNames, ANALYTICS_CATEGORY } from 'shared/constants'
import type { State } from 'shared/types/gallery/state'
import type { GalleryQuickView } from 'shared/types/gallery/quick-view'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { State as CurrentDesignState } from '~/app/features/Gallery/components/PreviewArea/GalleryQuickView/hooks/useCurrentDesign'

export const useNewQuickViewAnalyticsCallbacks = (tileEntity: State.TileEntity | null, productQuantity: number | undefined): QuickViewAnalyticsCallbacks => {
  const quickViewAnalytics = useQuickViewAnalytics()
  const analytics = useAnalytics()
  const impressionId = useSelector((state: State.GlobalState) => (tileEntity ? galleryImpressionIdSelector(state, tileEntity.designId) : ''))
  const isDirectQuickView = useSelector(getIsDirectQuickView)
  const isTakeOverQV = useSelector(getTakeOverQV)
  const dpcId = useSelector(designPersonalizationContextIdSelector)
  const quickViewFilters = useSelector(selectQuickViewFilterNames)
  const { locale, market } = useSelector(globalQuickViewDataSelector)
  const engagementIdRef = useRef<string>(uuid())
  const { designPersonalizationHasLogo, designPersonalizationHasPhoto } = usePersonalizationWidgetData()
  const startingEntity = tileEntity ?? ({} as State.TileEntity)
  const [currentProductOptions, setCurrentProductOptions] = useState<GalleryContentQuery.ProductOptions>(() => tileEntity?.productOptions ?? {})
  const currentEntityForPricing = { ...startingEntity, productOptions: currentProductOptions } as unknown as CurrentDesignState

  const { initialEstimatedPriceData, currentEstimatedPriceData } = useProductOptionsTracking({
    currentEntity: currentEntityForPricing,
    startingEntity,
    locale,
    market,
    quantity: productQuantity,
    enabled: !!tileEntity,
  })

  const buildCurrentEntity = useCallback(
    (currentDesign: DesignVariationResponse | SimilarTemplateEntity): GalleryQuickView.QuickViewCurrentDesignData | null => {
      const activeSwatch = currentDesign.colorSwatches.find(
        (swatch: GalleryContentQuery.ColorSwatch) => swatch.designId === currentDesign.selectedColorSwatchDesignId,
      )

      return {
        ...tileEntity,
        currentColorSwatch: activeSwatch,
        designId: currentDesign.designId,
        currentEntityId: currentDesign.designId,
        colorSwatches: currentDesign.colorSwatches,
        studioUrl: activeSwatch?.studioUrl,
        productOptions: currentDesign.productOptions,
        fullProductOptions: currentDesign.fullProductOptions,
        selectedColorSwatchDesignId: currentDesign.selectedColorSwatchDesignId,
        externalId: currentDesign.externalId,
        nonPersonalizedPreviewUrls: currentDesign.nonPersonalizedPreviewUrls,
        nonPersonalizedStudioUrl: currentDesign.nonPersonalizedStudioUrl,
        previewInfo: currentDesign.previewInfo,
        previewUrls: currentDesign.previewUrls,
      } as unknown as GalleryQuickView.QuickViewCurrentDesignData
    },
    [tileEntity],
  )

  const onQuickViewOpened = useCallback(
    (currentDesign: DesignVariationResponse): void => {
      const currentEntity = buildCurrentEntity(currentDesign)

      if (!currentEntity) return

      if (isDirectQuickView && !isTakeOverQV) {
        quickViewAnalytics.recordImpression(currentEntity, impressionId)
      }

      if (!isTakeOverQV) {
        analytics.trackEvent({
          action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
          eventLabel: ANALYTICS_LABEL.FLY_OUT_VIEWED,
          eventDetail: dpcId ? 'Quick view opened with personalized design' : '',
          ...analytics.buildDesignEngagement({
            engagementAction: ANALYTICS_LABEL.OPEN_QUICKVIEW,
            selectedDesign: currentEntity.currentDesignId,
            color: currentEntity.currentColorSwatch.color?.split('#')[1],
            tileEntity: currentEntity,
            colorSwatchObjects: currentEntity.colorSwatches,
            impressionId,
            location: AnalyticsNames.DesignTile,
            position: tileEntity?.position,
          }),
        })
      }
    },
    [buildCurrentEntity, isDirectQuickView, isTakeOverQV, dpcId, analytics, impressionId, tileEntity?.position, quickViewAnalytics],
  )

  const onColorSwatchChanged = useCallback(
    (toDesignId: string, currentDesign: DesignVariationResponse): void => {
      const currentEntity = buildCurrentEntity(currentDesign)

      if (!currentEntity) return

      if (!isTakeOverQV) {
        quickViewAnalytics.recordChangeColorSwatch(currentEntity, toDesignId, impressionId, dpcId ? 'Quick view color selection with personalized design' : '')
      }
    },
    [buildCurrentEntity, quickViewAnalytics, impressionId, dpcId, isTakeOverQV],
  )

  const onOptionChanged = useCallback(
    (optionName: string, oldValue: string, newValue: string, currentDesign: DesignVariationResponse): void => {
      const currentEntity = buildCurrentEntity(currentDesign)

      if (!currentEntity) return

      setCurrentProductOptions(currentEntity.productOptions)

      if (!isTakeOverQV) {
        const eventDetail = dpcId ? 'Quick view filter selection with personalized design' : ''
        quickViewAnalytics.recordChangeFilter(currentEntity, optionName, oldValue, newValue, productQuantity, impressionId, eventDetail)
      }
    },
    [buildCurrentEntity, quickViewAnalytics, impressionId, dpcId, productQuantity, isTakeOverQV],
  )

  const onSimilarTemplateClicked = useCallback(
    (entity: SimilarTemplateEntity): void => {
      const currentEntity = buildCurrentEntity(entity)
      if (!currentEntity) return

      analytics.trackEvent({
        action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
        category: ANALYTICS_CATEGORY.ALL,
        eventLabel: ANALYTICS_EVENT_DETAILS.MORE_LIKE_THIS_CLICKED,
        ...analytics.buildDesignEngagement({
          engagementAction: ANALYTICS_EVENT_DETAILS.MORE_LIKE_THIS_CLICKED,
          selectedDesign: currentEntity.currentDesignId,
          color: currentEntity.currentColorSwatch.color,
          tileEntity: currentEntity,
          colorSwatchObjects: currentEntity.colorSwatches,
          impressionId,
          location: AnalyticsNames.Quickview,
          position: currentEntity.position,
        }),
      })
    },
    [buildCurrentEntity, analytics, impressionId],
  )

  const onFavoriteToggled = useCallback(
    (isFavorite: boolean, currentDesign: DesignVariationResponse): void => {
      const currentEntity = buildCurrentEntity(currentDesign)

      if (!currentEntity) return

      if (!isTakeOverQV) {
        const eventLabel = isFavorite ? ANALYTICS_LABEL.FAVORITE_ADDED : ANALYTICS_LABEL.FAVORITE_REMOVED
        const eventDetail = isFavorite ? ANALYTICS_EVENT_DETAILS.FAVORITE_ADDED : ANALYTICS_EVENT_DETAILS.FAVORITE_REMOVED

        analytics.trackEvent({
          action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
          category: ANALYTICS_CATEGORY.ALL,
          eventLabel,
          eventDetail,
          ...analytics.buildDesignEngagement({
            engagementAction: eventDetail,
            selectedDesign: currentEntity.currentDesignId,
            color: currentEntity.currentColorSwatch?.color?.split('#')[1],
            tileEntity: currentEntity,
            colorSwatchObjects: currentEntity.colorSwatches,
            impressionId,
            location: AnalyticsNames.Quickview,
            position: currentEntity.position,
          }),
        })
      }
    },
    [analytics, buildCurrentEntity, impressionId, isTakeOverQV],
  )

  const onDesignSelected = useCallback(
    (currentDesign: DesignVariationResponse): void => {
      const currentEntity = buildCurrentEntity(currentDesign)

      if (!currentEntity) return

      let eventDetail = ''
      if (designPersonalizationHasLogo) {
        eventDetail = 'Studio opened with personalized logo'
      } else if (designPersonalizationHasPhoto) {
        eventDetail = 'Studio opened with personalized photo'
      }

      if (!isTakeOverQV) {
        quickViewAnalytics.recordSelectDesign(
          currentEntity,
          impressionId,
          startingEntity.productOptions,
          quickViewFilters,
          engagementIdRef.current,
          initialEstimatedPriceData,
          currentEstimatedPriceData,
          productQuantity,
          productQuantity,
          eventDetail,
        )
      }
    },
    [
      buildCurrentEntity,
      currentEstimatedPriceData,
      designPersonalizationHasLogo,
      designPersonalizationHasPhoto,
      impressionId,
      initialEstimatedPriceData,
      isTakeOverQV,
      productQuantity,
      quickViewFilters,
      quickViewAnalytics,
      startingEntity.productOptions,
    ],
  )

  return { onDesignSelected, onColorSwatchChanged, onSimilarTemplateClicked, onOptionChanged, onQuickViewOpened, onFavoriteToggled }
}
